import { module as BaseModule } from 'modujs'
import { gsap } from 'gsap'

const distance = (x, y) => Math.sqrt(x * x + y * y)
const interpolate = (c1, c2, f) => ({
  r: Math.floor(c1.r + (c2.r - c1.r) * f),
  g: Math.floor(c1.g + (c2.g - c1.g) * f),
  b: Math.floor(c1.b + (c2.b - c1.b) * f),
})

const makeFiveColorGradient = (c1, c2, c3, c4, c5) => {
  const g = []
  for (let i = 0; i < 64; i += 1) {
    const f = i / 64
    g[i] = interpolate(c1, c2, f)
  }
  for (let i = 64; i < 128; i += 1) {
    const f = (i - 64) / 64
    g[i] = interpolate(c2, c3, f)
  }
  for (let i = 128; i < 192; i += 1) {
    const f = (i - 128) / 64
    g[i] = interpolate(c3, c4, f)
  }
  for (let i = 192; i < 256; i += 1) {
    const f = (i - 192) / 64
    g[i] = interpolate(c4, c5, f)
  }

  return g
}

export default class extends BaseModule {
  init() {
    this.imgSize = 512
    this.mapSize = this.imgSize * 2
    this.rendering = false
    this.isComposing = true

    this.lightPalettes = [
      { r: 255, g: 144, b: 0 },
      { r: 255, g: 115, b: 76 },
      { r: 239, g: 124, b: 129 },
      { r: 79, g: 136, b: 180 },
      { r: 19, g: 123, b: 149 },
    ]

    this.darkPalette = [
      { r: 68, g: 68, b: 68 },
      { r: 40, g: 40, b: 40 },
      { r: 30, g: 30, b: 30 },
      { r: 30, g: 30, b: 30 },
      { r: 22, g: 22, b: 22 },
    ]

    this.lightPalette = [
      { r: 255, g: 144, b: 0 },
      { r: 255, g: 115, b: 76 },
      { r: 239, g: 124, b: 129 },
      { r: 79, g: 136, b: 180 },
      { r: 19, g: 123, b: 149 },
    ]

    this.darkPalettes = [
      { r: 158, g: 158, b: 158 },
      { r: 163, g: 163, b: 163 },
      { r: 239, g: 239, b: 239 },
      { r: 188, g: 188, b: 188 },
      { r: 86, g: 86, b: 86 },
    ]

    this.sources = this.lightPalette
    this.state = true

    document.addEventListener('grain', ({ detail }) => {
      const { active } = detail
      this.destinations = active ? this.darkPalettes : this.lightPalettes

      const transitionColors = []

      const animation = {
        progress: 0,
      }
      this.tween = gsap.to(animation, {
        duration: 0.5,
        progress: 1,
        onUpdate: () => {
          const value = this.tween.progress()
          this.destinations.forEach((dest, index) => {
            const newColor = interpolate(this.sources[index], dest, value)
            transitionColors[index] = newColor
          })

          this.syncDefinedPalette(
            transitionColors[0],
            transitionColors[1],
            transitionColors[2],
            transitionColors[3],
            transitionColors[4],
          )
        },
        onComplete: () => {
          this.transitionColors = []
          this.sources = this.destinations
        },
      })
    })

    this.initTexture()
  }

  initTexture() {
    this.ctx = this.el.getContext('2d')
    this.el.width = this.imgSize
    this.el.height = this.imgSize

    /* init image data with black pixels */
    this.image = this.ctx.createImageData(this.imgSize, this.imgSize)
    for (let i = 0; i < this.image.data.length; i += 4) {
      this.image.data[i] = 0 // R
      this.image.data[i + 1] = 0 // G
      this.image.data[i + 2] = 0 // B
      this.image.data[i + 3] = 255 // A
    }

    this.heightMap1 = []
    for (let u = 0; u < this.mapSize; u += 1) {
      for (let v = 0; v < this.mapSize; v += 1) {
        // index of coordinate in height map array
        const i = u * this.mapSize + v
        // u,v are coordinates with origin at upper left corner
        // cx and cy are coordinates with origin at the
        // center of the map
        const cx = u - this.mapSize / 2
        const cy = v - this.mapSize / 2
        // distance from middle of map
        const d = distance(cx, cy)
        // stretching so we get the desired ripple density on our map
        const stretch = (3 * Math.PI) / (this.mapSize / 2)
        // wavy height value between -1 and 1
        const ripple = Math.sin(d * stretch)
        // wavy height value normalized to 0..1
        const normalized = (ripple + 1) / 2
        // height map value 0..128, integer
        this.heightMap1[i] = Math.floor(normalized * 128)
      }
    }
    this.heightMap2 = []
    for (let u = 0; u < this.mapSize; u += 1) {
      for (let v = 0; v < this.mapSize; v += 1) {
        const i = u * this.mapSize + v
        const cx = u - this.mapSize / 2
        const cy = v - this.mapSize / 2
        // skewed distance as input to chaos field calculation,
        // scaled for smoothness over map distance
        const d1 = distance(0.8 * cx, 1.3 * cy) * 0.022
        const d2 = distance(1.35 * cx, 0.45 * cy) * 0.022
        const s = Math.sin(d1)
        const c = Math.cos(d2)
        // height value between -2 and +2
        const h = s + c
        // height value between 0..1
        const normalized = (h + 2) / 4
        // height value between 0..127, integer
        this.heightMap2[i] = Math.floor(normalized * 127)
      }
    }

    /* offsets for moving height maps */
    this.dx1 = 0
    this.dy1 = 0
    this.dx2 = 0
    this.dy2 = 0
    this.palettes = []
    this.makePalette()
    this.palette = []
    // two palettes we interpolate between
    // const palettes = [makeRandomPalette(), makeRandomPalette()]
    // current palette is edstablished durting animation
    // stores whether we're interpolating colors
    // from palette 0 -> 1 (1) or 1 -> 0 (-1)
    // const prevDirection = 1

    requestAnimationFrame(this.tick.bind(this))
  }

  tick(time) {
    this.moveHeightMaps(time)
    this.updatePalette(time)
    this.updateImageData()

    this.ctx.putImageData(this.image, 0, 0)

    if (!this.rendering) {
      this.rendering = true
    }

    requestAnimationFrame(this.tick.bind(this))
  }

  updateImageData() {
    for (let u = 0; u < this.imgSize; u += 1) {
      for (let v = 0; v < this.imgSize; v += 1) {
        // indexes into height maps for pixel
        const i = (u + this.dy1) * this.mapSize + (v + this.dx1)
        const k = (u + this.dy2) * this.mapSize + (v + this.dx2)

        // index for pixel in image data
        // remember it's 4 bytes per pixel
        const j = u * this.imgSize * 4 + v * 4

        // height value of 0..255
        const h = this.heightMap1[i] + this.heightMap2[k]
        // get color value from current palette
        const c = this.palette[h]

        // h = this.heightMap2[i];
        // c = { r: h, g: h, b: h };

        // set pixel data
        this.image.data[j] = c.r
        this.image.data[j + 1] = c.g
        this.image.data[j + 2] = c.b
      }
    }
  }

  makeDefinedPalette(c1, c2, c3, c4, c5) {
    return makeFiveColorGradient(c1, c2, c3, c4, c5)
  }

  // returns a random color palette with 256 color entries
  makeRandomPalette() {
    // const c1 = { r: 255, g: 103, b: 42 }
    // const c2 = { r: 255, g: 115, b: 76 }
    // const c3 = { r: 239, g: 124, b: 129 }
    // const c4 = { r: 79, g: 136, b: 180 }
    // const c5 = { r: 19, g: 123, b: 149 }

    const c1 = this.sources[0]
    const c2 = this.sources[1]
    const c3 = this.sources[2]
    const c4 = this.sources[3]
    const c5 = this.sources[4]
    return makeFiveColorGradient(c1, c2, c3, c4, c5)
  }

  syncDefinedPalette(c1, c2, c3, c4, c5) {
    this.palettes[0] = this.makeDefinedPalette(c1, c2, c3, c4, c5)
    this.palettes[1] = this.makeDefinedPalette(c1, c2, c3, c4, c5)
  }

  makePalette() {
    this.palettes[0] = this.makeRandomPalette()
    this.palettes[1] = this.makeRandomPalette()
  }

  updatePalette(t) {
    const timeScale = 2
    const x = t * timeScale * 0.0005
    // normalized value 0..1 used to interpolate palette colors
    const inter = x

    // did we switch direction, and should ergo pick a new palette
    // random palette to interpolate towards?

    // const direction = -Math.sin(x) >= 0 ? 1 : -1;
    // if (prevDirection != direction) {
    //   prevDirection = direction;
    //   if (direction == -1) {
    //     palettes[0] = makeRandomPalette();
    //   } else {
    //     palettes[1] = makeRandomPalette();
    //   }
    // }

    // create interpolated palette for current frame
    for (let i = 0; i < 256; i += 1) {
      this.palette[i] = interpolate(this.palettes[0][i], this.palettes[1][i], inter)
    }
  }

  // adjust height maps offsets
  moveHeightMaps(t) {
    t *= 0.4
    this.dx1 = Math.floor((((Math.cos(t * 0.0002 + 0.4 + Math.PI) + 1) / 2) * this.mapSize) / 2)
    this.dy1 = Math.floor((((Math.cos(t * 0.0003 - 0.1) + 1) / 2) * this.mapSize) / 2)
    this.dx2 = Math.floor((((Math.cos(t * -0.0002 + 1.2) + 1) / 2) * this.mapSize) / 2)
    this.dy2 = Math.floor((((Math.cos(t * -0.0003 - 0.8 + Math.PI) + 1) / 2) * this.mapSize) / 2)
  }
}
